import { Head, Link, useForm } from '@inertiajs/react';
import { type FormEvent } from 'react';

import {
    Btn,
    DesignFooter,
    DesignHeader,
    I,
    TrustBadge,
    UtilityBar,
} from '@/Components/Design';

type StateOpt = { slug: string; name: string };
type PracticeOpt = { slug: string; name: string; icon: string | null };
type Props = {
    states: StateOpt[];
    practiceAreas: PracticeOpt[];
    turnstileSiteKey: string;
};

const inputStyle: React.CSSProperties = {
    height: 52,
    padding: '0 16px',
    border: '1px solid var(--rule)',
    borderRadius: 10,
    background: 'var(--card)',
    fontSize: 16,
    outline: 0,
    fontFamily: 'inherit',
    color: 'var(--ink)',
    width: '100%',
};

export default function GetMatched({ states, practiceAreas, turnstileSiteKey }: Props) {
    const form = useForm({
        name: '',
        email: '',
        phone: '',
        state: '',
        city: '',
        practiceArea: '',
        description: '',
        consent: true,
        nickname: '',
        'cf-turnstile-response': '',
    });

    function submit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const token = new FormData(e.currentTarget).get('cf-turnstile-response');

        form.transform((data) => ({
            ...data,
            'cf-turnstile-response': typeof token === 'string' ? token : '',
        }));
        form.post('/get-matched');
    }

    return (
        <>
            <Head>
                <title>Get matched with a personal injury lawyer in minutes</title>
            </Head>
            <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
                <UtilityBar />
                <DesignHeader active="Find a Lawyer" />

                <section data-getmatched-section style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 56px 64px' }}>
                    <nav aria-label="Breadcrumb">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-mute)', marginBottom: 18 }}>
                        <Link href="/" style={{ color: 'inherit', textDecoration: 'none' }}>Home</Link>
                        <span>/</span>
                        <span aria-current="page" style={{ color: 'var(--ink)' }}>Get matched</span>
                    </div></nav>

                    <div data-getmatched-grid style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ember)', marginBottom: 10, fontWeight: 600 }}>Get matched</div>
                            <h1 style={{ fontSize: 56, lineHeight: 1.05, letterSpacing: '-0.025em', marginBottom: 20, fontFamily: 'var(--font-serif)', fontWeight: 500 }}>
                                Tell us what happened. We'll <em>match you</em> with up to three local attorneys.
                            </h1>
                            <p style={{ fontSize: 18, color: 'var(--ink-soft)', lineHeight: 1.55, marginBottom: 28 }}>
                                Plain English. One question per screen. We send your info straight to the attorney — not a call center.
                            </p>
                            <div data-trust-row style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <TrustBadge tone="sage" icon={I.check} label="Free, with no obligation" />
                                <TrustBadge tone="sage" icon={I.check} label="Bar-verified attorneys only" />
                                <TrustBadge tone="sage" icon={I.check} label="Average response: under 1 hour" />
                                <TrustBadge tone="sage" icon={I.check} label="Bilingual intake counselors" />
                            </div>
                        </div>

                        <form onSubmit={submit} data-card style={{ background: 'var(--card)', border: '1px solid var(--rule)', borderRadius: 16, padding: 32, boxShadow: 'var(--shadow-card)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <Field label="What kind of case?">
                                    <select value={form.data.practiceArea} onChange={(e) => form.setData('practiceArea', e.target.value)} required style={inputStyle}>
                                        <option value="">Select case type…</option>
                                        {practiceAreas.map((p) => (
                                            <option key={p.slug} value={p.slug}>{p.name}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="State">
                                    <select value={form.data.state} onChange={(e) => form.setData('state', e.target.value)} required style={inputStyle}>
                                        <option value="">Select state…</option>
                                        {states.map((s) => (
                                            <option key={s.slug} value={s.slug}>{s.name}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="City (optional)">
                                    <input value={form.data.city} onChange={(e) => form.setData('city', e.target.value)} placeholder="e.g. Lakeland" style={inputStyle} />
                                </Field>
                                <Field label="Your name">
                                    <input value={form.data.name} onChange={(e) => form.setData('name', e.target.value)} required style={inputStyle} />
                                </Field>
                                <Field label="Email">
                                    <input type="email" value={form.data.email} onChange={(e) => form.setData('email', e.target.value)} required style={inputStyle} />
                                </Field>
                                <Field label="Phone">
                                    <input value={form.data.phone} onChange={(e) => form.setData('phone', e.target.value)} placeholder="(optional)" style={inputStyle} />
                                </Field>
                                <Field label="Briefly, what happened?">
                                    <textarea
                                        value={form.data.description}
                                        onChange={(e) => form.setData('description', e.target.value)}
                                        rows={4}
                                        placeholder="A sentence or two — we'll figure the rest out at the consult."
                                        style={{ ...inputStyle, height: 'auto', padding: '14px 16px', resize: 'vertical', fontFamily: 'inherit' }}
                                    />
                                </Field>

                                {turnstileSiteKey && (
                                    <div className="cf-turnstile" data-sitekey={turnstileSiteKey} />
                                )}

                                {/* Honeypot */}
                                <input
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={form.data.nickname}
                                    onChange={(e) => form.setData('nickname', e.target.value)}
                                    style={{ position: 'absolute', left: '-10000px' }}
                                    aria-hidden
                                />

                                <Btn kind="primary" size="xl" full type="submit">
                                    Find my lawyer
                                </Btn>
                                <div style={{ fontSize: 11, color: 'var(--ink-mute)', lineHeight: 1.5, textAlign: 'center' }}>
                                    Submitting does not create an attorney-client relationship.
                                </div>
                            </div>
                        </form>
                    </div>
                </section>

                <DesignFooter />
            </div>
        </>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', letterSpacing: '.06em', textTransform: 'uppercase' }}>{label}</span>
            {children}
        </label>
    );
}
